<template>
	<div class="candidate-actions" v-if="!isReadOnlyUser">
		<template v-if="hasActiveApplications">
			<v-menu
				v-if="activeApplications.length > 1"
				offset-y
				bottom
			>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						small
						elevation="0"
						class="mr-2 candidate-actions__btn candidate-actions__btn--hire"
						v-bind="attrs"
						v-on="on"
					>
						<v-icon left small>thumb_up</v-icon>
						{{ $t('button.hire-action').toLocaleUpperCase(locale) }}
						<v-icon right small>arrow_drop_down</v-icon>
					</v-btn>
				</template>
				<v-list dense>
					<v-list-item
						v-for="app in activeApplications"
						:key="app.id"
						@click="handleHireClick(app)"
					>
						<v-list-item-title>
							{{ getJobName(app.job_id) }}
						</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-btn
				v-else
				small
				elevation="0"
				class="mr-2 candidate-actions__btn candidate-actions__btn--hire"
				@click="handleHireClick(activeApplications[0])"
			>
				<v-icon left small>thumb_up</v-icon>
				{{ $t('button.hire-action').toLocaleUpperCase(locale) }}
			</v-btn>

			<v-menu
				v-if="activeApplications.length > 1"
				offset-y
				bottom
			>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						small
						elevation="0"
						class="mr-2 candidate-actions__btn candidate-actions__btn--reject"
						v-bind="attrs"
						v-on="on"
					>
						<v-icon left small>thumb_down</v-icon>
						{{ $t('button.reject-action').toLocaleUpperCase(locale) }}
						<v-icon right small>arrow_drop_down</v-icon>
					</v-btn>
				</template>
				<v-list dense>
					<v-list-item
						v-for="app in activeApplications"
						:key="app.id"
					>
						<v-menu
							offset-x
							open-on-hover
							right
						>
							<template v-slot:activator="{ on, attrs }">
								<v-list-item-title
									v-bind="attrs"
									v-on="on"
									style="display: flex; align-items: center; justify-content: space-between; cursor: pointer;"
								>
									{{ getJobName(app.job_id) }}
									<v-icon small class="ml-2">chevron_right</v-icon>
								</v-list-item-title>
							</template>
							<v-list dense>
								<v-list-item
									v-for="reason in rejectReasons"
									:key="reason"
									@click="handleRejectClick(app, reason)"
								>
									<v-list-item-title>
										{{ $t(`pipeline.steps.${reason}`) }}
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-menu
				v-else
				offset-y
				bottom
			>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						small
						elevation="0"
						class="mr-2 candidate-actions__btn candidate-actions__btn--reject"
						v-bind="attrs"
						v-on="on"
					>
						<v-icon left small>thumb_down</v-icon>
						{{ $t('button.reject-action').toLocaleUpperCase(locale) }}
						<v-icon right small>arrow_drop_down</v-icon>
					</v-btn>
				</template>
				<v-list dense>
					<v-list-item
						v-for="reason in rejectReasons"
						:key="reason"
						@click="handleRejectClick(activeApplications[0], reason)"
					>
						<v-list-item-title>
							{{ $t(`pipeline.steps.${reason}`) }}
						</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-menu
				v-if="activeApplications.length === 1"
				offset-y
				bottom
			>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						icon
						small
						v-bind="attrs"
						v-on="on"
					>
						<v-icon>more_vert</v-icon>
					</v-btn>
				</template>
				<v-list dense>
					<v-subheader>{{ $t('subheader.lead') }}</v-subheader>
					<v-list-item
						v-for="item in pipelineLeadItems"
						:key="'lead-' + item"
						@click="handleStatusChange(activeApplications[0], {
							text: item,
							value: item,
							is_lead: true,
							is_interview: false,
							is_archived: false,
						})"
					>
						<v-list-item-title>{{ $t(`pipeline.steps.${item}`) }}</v-list-item-title>
					</v-list-item>
					<v-divider></v-divider>
					<v-subheader>{{ $t('subheader.application') }}</v-subheader>
					<v-list-item
						@click="handleStatusChange(activeApplications[0], {
							text: 'new-applicant',
							value: 'new-applicant',
							is_lead: false,
							is_interview: false,
							is_archived: false,
						})"
					>
						<v-list-item-title>{{ $t('pipeline.steps.new-applicant') }}</v-list-item-title>
					</v-list-item>
					<v-divider></v-divider>
					<v-subheader>{{ $t('subheader.interview') }}</v-subheader>
					<v-list-item
						v-for="item in pipelineInterviewItems"
						:key="'interview-' + item"
						@click="handleStatusChange(activeApplications[0], {
							text: item,
							value: item,
							is_lead: false,
							is_interview: true,
							is_archived: false,
						})"
					>
						<v-list-item-title>{{ $t(`pipeline.steps.${item}`) }}</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-menu
				v-else
				offset-y
				bottom
			>
				<template v-slot:activator="{ on, attrs }">
					<v-btn
						icon
						small
						v-bind="attrs"
						v-on="on"
					>
						<v-icon>more_vert</v-icon>
					</v-btn>
				</template>
				<v-list dense>
					<v-list-item
						v-for="app in activeApplications"
						:key="'more-' + app.id"
					>
						<v-menu
							offset-x
							open-on-hover
							right
						>
							<template v-slot:activator="{ on, attrs }">
								<v-list-item-title
									v-bind="attrs"
									v-on="on"
									style="display: flex; align-items: center; justify-content: space-between; cursor: pointer;"
								>
									{{ getJobName(app.job_id) }}
									<v-icon small class="ml-2">chevron_right</v-icon>
								</v-list-item-title>
							</template>
							<v-list dense>
								<v-subheader>{{ $t('subheader.lead') }}</v-subheader>
								<v-list-item
									v-for="item in pipelineLeadItems"
									:key="'lead-' + item"
									@click="handleStatusChange(app, {
										text: item,
										value: item,
										is_lead: true,
										is_interview: false,
										is_archived: false,
									})"
								>
									<v-list-item-title>{{ $t(`pipeline.steps.${item}`) }}</v-list-item-title>
								</v-list-item>
								<v-divider></v-divider>
								<v-subheader>{{ $t('subheader.application') }}</v-subheader>
								<v-list-item
									@click="handleStatusChange(app, {
										text: 'new-applicant',
										value: 'new-applicant',
										is_lead: false,
										is_interview: false,
										is_archived: false,
									})"
								>
									<v-list-item-title>{{ $t('pipeline.steps.new-applicant') }}</v-list-item-title>
								</v-list-item>
								<v-divider></v-divider>
								<v-subheader>{{ $t('subheader.interview') }}</v-subheader>
								<v-list-item
									v-for="item in pipelineInterviewItems"
									:key="'interview-' + item"
									@click="handleStatusChange(app, {
										text: item,
										value: item,
										is_lead: false,
										is_interview: true,
										is_archived: false,
									})"
								>
									<v-list-item-title>{{ $t(`pipeline.steps.${item}`) }}</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</v-list-item>
				</v-list>
			</v-menu>
		</template>

		<Confirm
			v-if="isHireConfirmVisible"
			:onConfirm="handleConfirmHire"
			:onClose="handleHireConfirmClose"
			primaryActionColor="success"
		>
			<div style="padding-top: 20px">
				{{ $t('text.hire-candidate-confirmation', {
					jobName: selectedApplicationJobName,
				}) }}
			</div>
		</Confirm>

		<Confirm
			v-if="isStatusConfirmVisible"
			:onConfirm="handleConfirmStatusChange"
			:onClose="handleStatusConfirmClose"
		>
			<div
				v-html="$t('text.edit-app-status', {
					initialStatus: `<strong>${$t(`pipeline.steps.${statusChangeInitial}`)}</strong>`,
					nextStatus: `<strong>${$t(`pipeline.steps.${statusChangeNext}`)}</strong>`,
				})"
				style="padding-top: 20px"
			></div>
		</Confirm>

		<div
			v-if="showLottie"
			class="lottie-overlay"
		>
			<div ref="lottieContainer" class="lottie-container"></div>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import lottie from 'lottie-web';
import Confirm from '@/components/shared/dialogs/confirm/Confirm';
import hireSuccessAnimation from '@/assets/animations/hire-success.json';

export default {
	name: 'CandidateActions',
	components: {
		Confirm,
	},
	props: {
		candidate: {
			type: Object,
			required: true,
		},
		onArchive: {
			type: Function,
			required: true,
		},
	},
	data() {
		return {
			isHireConfirmVisible: false,
			isStatusConfirmVisible: false,
			selectedApplication: null,
			statusChangeApplication: null,
			statusChangeObject: null,
			statusChangeInitial: null,
			statusChangeNext: null,
			showLottie: false,
			pendingLottie: false,
			rejectReasons: [
				'underqualified',
				'not-interested',
				'withdrew',
				'offer-declined',
				'future',
				'blacklist',
				'unresponded',
			],
		};
	},
	computed: {
		...mapState({
			jobs: state => state.jobs.items,
			currentLang: state => state.currentUser.lang,
			isLoading: state => state.apiTraffic.isLoading,
			pipelineInterviewItems: state => state.pipelineInterview.items,
			pipelineLeadItems: state => state.pipelineLead.items,
		}),
		...mapGetters({
			isReadOnlyUser: 'currentUser/isReadOnlyUser',
		}),
		locale() {
			return this.currentLang === 'tr' ? 'tr-TR' : 'en-US';
		},
		activeApplications() {
			if (!this.candidate || !this.candidate.applications) return [];
			return this.candidate.applications.filter(app => !app.is_archived);
		},
		hasActiveApplications() {
			return this.activeApplications.length > 0;
		},
		selectedApplicationJobName() {
			if (!this.selectedApplication) return '';
			return this.getJobName(this.selectedApplication.job_id);
		},
	},
	methods: {
		getJobName(jobId) {
			const job = this.jobs.find(item => item.id === jobId);
			return job ? job.name : jobId;
		},
		getApplicationStatus(application) {
			const { is_interview, interview_status, is_lead, lead_status, is_archived, archive_status } = application;
			if (is_archived) return archive_status || 'underqualified';
			if (is_interview) return interview_status || 'phone';
			if (is_lead) return lead_status || 'new-lead';
			return 'new-applicant';
		},
		handleHireClick(application) {
			this.selectedApplication = application;
			this.isHireConfirmVisible = true;
		},
		handleConfirmHire() {
			this.isHireConfirmVisible = false;
			const { selectedApplication } = this;
			if (!selectedApplication) return;

			this.$store
				.dispatch('applicants/put', {
					items: [
						{
							id: selectedApplication.id,
							is_interview: false,
							interview_status: null,
							is_archived: true,
							archive_status: 'archive-hired',
							is_lead: false,
							lead_status: null,
							updateStore: false,
						},
					],
				})
				.then(() => {
					this.$store.commit('candidates/setItemsNeedToRefresh');
					this.$store.commit('candidates/setItemNeedsToRefresh');
					this.pendingLottie = true;
					this.$emit('hired', {
						applicationId: selectedApplication.id,
						jobId: selectedApplication.job_id,
					});
				});
		},
		handleHireConfirmClose() {
			this.isHireConfirmVisible = false;
			this.selectedApplication = null;
		},
		handleRejectClick(application, reason) {
			this.onArchive({
				job_id: application.job_id,
				application_id: application.id,
				prev_status: this.getApplicationStatus(application),
				archive_reason: reason,
			});
		},
		handleStatusChange(application, statusObj) {
			if (statusObj.is_archived) {
				this.onArchive({
					job_id: application.job_id,
					application_id: application.id,
					prev_status: this.getApplicationStatus(application),
					archive_reason: statusObj.value,
				});
			} else {
				this.statusChangeApplication = application;
				this.statusChangeObject = { ...statusObj };
				this.statusChangeInitial = this.getApplicationStatus(application);
				this.statusChangeNext = statusObj.value;
				this.isStatusConfirmVisible = true;
			}
		},
		handleConfirmStatusChange() {
			this.isStatusConfirmVisible = false;
			const { statusChangeApplication, statusChangeObject } = this;
			if (!statusChangeApplication || !statusChangeObject) return;

			const { is_interview, is_lead, value } = statusChangeObject;
			const interview_status = is_interview ? value : null;
			const lead_status = is_lead ? value : null;

			this.$store
				.dispatch('applicants/put', {
					items: [
						{
							id: statusChangeApplication.id,
							is_interview,
							interview_status,
							is_archived: false,
							archive_status: null,
							is_lead,
							lead_status,
							updateStore: false,
						},
					],
				})
				.then(() => {
					this.$store.commit('candidates/setItemsNeedToRefresh');
					this.$store.commit('candidates/setItemNeedsToRefresh');
					this.$emit('status-changed', {
						applicationId: statusChangeApplication.id,
						jobId: statusChangeApplication.job_id,
						prevStatus: this.statusChangeInitial,
						nextStatus: value,
					});
				});
		},
		handleStatusConfirmClose() {
			this.isStatusConfirmVisible = false;
			this.statusChangeApplication = null;
			this.statusChangeObject = null;
		},
		checkPendingLottie() {
			if (this.pendingLottie && !this.isLoading) {
				this.pendingLottie = false;
				this.playLottieAnimation();
			}
		},
		playLottieAnimation() {
			this.showLottie = true;
			this.$nextTick(() => {
				const anim = lottie.loadAnimation({
					container: this.$refs.lottieContainer,
					renderer: 'svg',
					loop: false,
					autoplay: true,
					animationData: hireSuccessAnimation,
				});
				anim.addEventListener('complete', () => {
					this.showLottie = false;
				});
				setTimeout(() => {
					this.showLottie = false;
				}, 4000);
			});
		},
	},
	watch: {
		isLoading(val) {
			if (!val) {
				this.checkPendingLottie();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.candidate-actions {
	display: flex;
	align-items: center;
	margin-left: 1rem;
}

.candidate-actions__btn {
	border-style: solid;
	text-transform: none;
	font-weight: 500;
	letter-spacing: normal;
}

.candidate-actions__btn--hire {
	background-color: rgba(46, 125, 50, 0.12) !important;
	border-width: 0.2px;
	border-color: #2e7d32;
	color: #2e7d32 !important;
	box-shadow:
		0 1px 5px 0 rgba(46, 125, 50, 0.12),
		0 2px 2px 0 rgba(46, 125, 50, 0.14);

	&:hover {
		background-color: rgba(46, 125, 50, 0.18) !important;
	}
}

.candidate-actions__btn--reject {
	background-color: rgba(183, 28, 28, 0.12) !important;
	border-width: 0.2px;
	border-color: #b71c1c;
	color: #b71c1c !important;
	box-shadow:
		0 1px 5px 0 rgba(183, 28, 28, 0.12),
		0 2px 2px 0 rgba(183, 28, 28, 0.14);

	&:hover {
		background-color: rgba(183, 28, 28, 0.18) !important;
	}
}

.candidate-actions__btn ::v-deep .v-icon {
	color: currentColor !important;
}

.lottie-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 999;
	pointer-events: none;
	display: flex;
	align-items: center;
	justify-content: center;
}

.lottie-container {
	width: 400px;
	height: 400px;
}
</style>
